/** @format */

import { faBars, faCalendarWeek } from '@fortawesome/pro-duotone-svg-icons';
import { faCog, faTools } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Badge,
  Chip,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
} from '@mui/material';
import AppBar from '@mui/material/AppBar';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/styles';
import { useSnackbar } from 'notistack';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router';
import { Link } from 'react-router-dom';
import { api, getApiUrl } from '../../api.ts';
import manywattLogo from '../../assets/images/logos/manywatts_light.png';
import { logout, selectAuth } from '../../redux/authSlice';
import { ThemeProvider } from '../../theme';
import CacheManager from '../../utils/cache';
import {
  MESSAGE_QUEUE_UPDATED,
  UNASSIGNED_LEADS_UPDATED,
  useUserWebSocket,
} from '../../websocket/UserWebSocket';
import FollowUpToggleButtonGroup from './followUp/FollowUpToggleButtonGroup';
import PlanningPresenceAvatars from './planning/components/PlanningPresenceAvatars';

const styles = (theme) => ({
  title: {
    display: 'none',
    [theme.breakpoints.up('md')]: {
      display: 'block',
    },
  },
  subTitle: {
    display: 'none',
    [theme.breakpoints.up('md')]: {
      display: 'block',
    },
  },
  grow: {
    flexGrow: 1,
  },
});

const StyledToolbar = styled(Toolbar)(({ theme }) => ({
  paddingLeft: theme.spacing(2),
  paddingRight: theme.spacing(2),
  overflow: 'visible',
}));

const StyledLogo = styled('img')(({ theme }) => ({
  height: 60,
  width: 'auto',
  maxWidth: 'none',
  flexShrink: 0,
  paddingTop: theme.spacing(1),
  paddingBottom: theme.spacing(1),
}));

const MenuButtonIcon = ({ light, ...iconProps }) => (
  <FontAwesomeIcon {...iconProps} />
);
const StyledMenuButtonIcon = styled(MenuButtonIcon)(({ theme, light }) => ({
  marginRight: theme.spacing(1),
  color: light ? '#408b6a' : undefined,
}));

const navButtonSx = {
  flexShrink: 0,
  whiteSpace: 'nowrap',
  boxShadow: 'none',
};

const StyledBadge = styled(Badge)(({ theme }) => ({
  '& > .MuiBadge-badge': {
    right: -12.5,
    top: '45%',
    border: `2px solid ${theme.palette.background.paper}`,
    padding: '0 4px',
  },
}));

const drawerWidth = 240;

const navItems = ({ user, logoutAction }) => [
  // {
  //   label: 'RDV du jour',
  //   path: '/mobile-funnel-page',
  //   permission: user.is_project_manager,
  //   highlight: true,
  // },
  // {
  //   label: 'PORTAIL',
  //   path: '/gate',
  //   permission: user.has_access_to_gate,
  //   highlight: true,
  // },
  {
    label: 'Mon planning',
    path: '/my-planning',
    permission: Boolean(
      user.is_visible_on_planning ||
        user.is_superuser ||
        user.is_planning_leader,
    ),
  },
  // {
  //   label: 'Leads',
  //   path: '/leads',
  //   permission: !user.is_marketing_only,
  // },
  // {
  //   label: 'Clients',
  //   path: '/clients',
  //   permission: true,
  // },
  {
    label: 'Se déconnecter',
    action: logoutAction,
    permission: true,
  },
];

function MainMenu() {
  const [anchorEl, setAnchorEl] = useState(null);
  const [anchorEl2, setAnchorEl2] = useState(null);
  const [toolsMenuOpen, setToolsMenuOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [messageQueueCount, setMessageQueueCount] = useState(0);
  const [unassignedLeadCount, setUnassignedLeadCount] = useState(0);

  const dispatch = useDispatch();
  const { user } = useSelector(selectAuth);
  const navigate = useNavigate();
  const location = useLocation();
  const isClientsNavActive =
    location.pathname === '/clients' ||
    location.pathname.startsWith('/clients/');
  const isPlanningNavActive =
    location.pathname === '/planning' ||
    location.pathname.startsWith('/planning/');
  const { enqueueSnackbar } = useSnackbar();
  const { isWSConnected } = useUserWebSocket();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(false);
  const isProjectManager = user.is_project_manager;

  const container = useMemo(
    () => (window !== undefined ? () => window.document.body : undefined),
    [],
  );

  const loadMessageQueueCount = () => {
    api
      .get('/automaticmessagequeueentry/count/')
      .then((res) => {
        setMessageQueueCount(res.data.count);
      })
      .catch((err) => {
        setMessageQueueCount(0);
        console.error(err);
      });
  };

  const loadUnassignedLeadCount = () => {
    api
      .get('/leads/unassigned_count/')
      .then((res) => {
        setUnassignedLeadCount(res.data.count);
      })
      .catch((err) => {
        setUnassignedLeadCount(0);
        console.error(err);
      });
  };

  // useEffect(() => {
  //   setLoading(true);
  //   const projectManager = user.is_project_manager;
  //   api
  //     .get(
  //       `leads/?filter=True${
  //         projectManager ? `&project_manager=${user.id}` : ''
  //       }&is_test=false&is_follow_up=true&limit=50`,
  //     )
  //     .then((res) => {
  //       console.log('leads MainMenu', res.data.results);
  //       setLeads(res.data.results);
  //     })
  //     .catch((err) => {
  //       console.error(err);
  //     }, []);
  //   setLoading(false);
  // }, []);

  useEffect(() => {
    if (isProjectManager) {
      setLoading(true);
      const fetchAllPages = async (url, accumulatedResults = []) => {
        try {
          const response = await api.get(url);
          const newResults = [...accumulatedResults, ...response.data.results];

          if (response.data.next && process.env.NODE_ENV !== 'development') {
            return fetchAllPages(response.data.next, newResults);
          }

          return newResults;
        } catch (err) {
          console.error(err);
          return accumulatedResults;
        }
      };

      const baseUrl = `leads/?filter=True${
        isProjectManager ? `&project_manager=${user.id}` : ''
      }&is_test=false&is_follow_up=true`;

      fetchAllPages(baseUrl).then((allResults) => {
        setLeads(allResults);
        setLoading(false);
      });
    }
  }, []);

  /* useMemo(() => {
    const result = location.pathname.match(/^\/lead\/(\d+).* /i);
    if (result && result.length > 1) {
      const leadId = result[1];
      window.YTFeedbackForm.getClientJSApi(
        'c99e083f-bacc-446b-b903-e48f56ada531',
      ).then(async (form) => {
        console.log('form set');
        const blocks = form.getBlocks();
        const leadIdBlock = blocks.find(
          (b) => b.type === 'integer' && b.title.includes('lead'),
        );
        if (location.pathname.match(/^\/lead\/(\d+).* /i)) {
          form.setBlockValue(leadIdBlock.title, leadId);
        } else {
          form.setBlockValue(leadIdBlock.title, '');
        }
      });
    } else {
      window.YTFeedbackForm.getClientJSApi(
        'c99e083f-bacc-446b-b903-e48f56ada531',
      ).then(async (form) => {
        console.log('form delete');
        const blocks = form.getBlocks();
        const leadIdBlock = blocks.find(
          (b) => b.type === 'integer' && b.title.includes('lead'),
        );
        form.setBlockValue(leadIdBlock.id, '');
      });
    }
  }, [location.pathname]); */

  const skipToolsButtonClickRef = useRef(false);
  const skipToolsButtonClickTimeoutRef = useRef(null);

  const suppressNextToolsButtonClick = () => {
    skipToolsButtonClickRef.current = true;
    window.clearTimeout(skipToolsButtonClickTimeoutRef.current);
    skipToolsButtonClickTimeoutRef.current = window.setTimeout(() => {
      skipToolsButtonClickRef.current = false;
    }, 400);
  };

  const handleMenu = (event) => {
    setToolsMenuOpen(false);
    setAnchorEl(event.currentTarget);
  };

  const handleMenu2 = (event) => {
    if (skipToolsButtonClickRef.current) {
      skipToolsButtonClickRef.current = false;
      return;
    }
    setAnchorEl(null);
    setAnchorEl2(event.currentTarget);
    setToolsMenuOpen(true);
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleClose = () => {
    suppressNextToolsButtonClick();
    setAnchorEl(null);
    setToolsMenuOpen(false);
  };

  const handleToolsNavigate = (path) => () => {
    suppressNextToolsButtonClick();
    setToolsMenuOpen(false);
    navigate(path);
  };

  useEffect(() => {
    setToolsMenuOpen(false);
    setAnchorEl(null);
  }, [location.pathname, location.search]);

  const handleLogout = () => {
    handleClose();
    api
      .post('/logout/')
      .then(() => {
        CacheManager.instance.invalidateModel('leads-status');
        dispatch(logout());
        if (
          window.google &&
          window.google.accounts &&
          window.google.accounts.id
        ) {
          window.google.accounts.id.disableAutoSelect();
        }
        navigate('/login');
        enqueueSnackbar('Déconnexion réussie.', { variant: 'success' });
      })
      .catch((err) => {
        console.error(err);
        enqueueSnackbar('Erreur : déconnexion impossible.', {
          variant: 'error',
        });
      });
  };

  useEffect(() => {
    loadMessageQueueCount();
    loadUnassignedLeadCount();
    window.addEventListener(MESSAGE_QUEUE_UPDATED, loadMessageQueueCount);
    window.addEventListener(UNASSIGNED_LEADS_UPDATED, loadUnassignedLeadCount);
    return () => {
      window.removeEventListener(MESSAGE_QUEUE_UPDATED, loadMessageQueueCount);
      window.removeEventListener(
        UNASSIGNED_LEADS_UPDATED,
        loadUnassignedLeadCount,
      );
    };
  }, []);

  return (
    <ThemeProvider>
      <Box component="nav">
        <Drawer
          container={container}
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile.
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
            },
          }}
        >
          <Box onClick={handleDrawerToggle} sx={{ textAlign: 'center' }}>
            <Typography variant="h6" sx={{ my: 2 }}>
              CRM
            </Typography>
            <Divider />
            <List>
              {navItems({
                user,
                logoutAction: handleLogout,
              })
                .filter((item) => item.permission)
                .map((item) => (
                  <ListItem key={item.label} disablePadding>
                    <ListItemButton
                      sx={{
                        textAlign: 'center',
                        backgroundColor: item.highlight
                          ? 'error.main'
                          : undefined,
                        color: item.highlight
                          ? 'error.contrastText'
                          : undefined,
                      }}
                      component={item.path ? Link : undefined}
                      to={item.path ? item.path : undefined}
                      onClick={item.action ? item.action : undefined}
                    >
                      <ListItemText primary={item.label} />
                    </ListItemButton>
                  </ListItem>
                ))}
            </List>
          </Box>
        </Drawer>
      </Box>
      <AppBar position="fixed" color="default">
        <StyledToolbar>
          <Box
            sx={{
              display: 'flex',
              width: '100%',
              alignItems: 'center',
              gap: 2,
            }}
          >
            <Box
              sx={{
                flex: '1 1 0',
                display: 'flex',
                alignItems: 'center',
                minWidth: 0,
              }}
            >
              <Link to="/">
                <StyledLogo src={manywattLogo} alt="" />
              </Link>
            </Box>

            {!user.is_marketing_only && (
              <Box
                sx={{
                  flex: '0 0 auto',
                  flexShrink: 0,
                  display: { xs: 'none', sm: 'flex' },
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 3,
                  minWidth: 'max-content',
                  '& > *': {
                    flexShrink: 0,
                  },
                }}
              >
                <FollowUpToggleButtonGroup
                  leads={leads}
                  loading={loading}
                  isProjectManager={isProjectManager}
                  unassignedLeadCount={unassignedLeadCount}
                  view="standard"
                />
                <Button
                  color={isClientsNavActive ? 'primary' : 'default'}
                  variant="contained"
                  to="/clients"
                  component={Link}
                  disableElevation
                  sx={navButtonSx}
                >
                  <StyledMenuButtonIcon
                    icon={faTools}
                    light={!isClientsNavActive}
                  />
                  <span
                    style={{
                      color: isClientsNavActive ? '' : '#408b6a',
                    }}
                  >
                    Clients
                  </span>
                </Button>
                {!user.is_blocked_from_company_planning && (
                  <Button
                    variant="contained"
                    color={isPlanningNavActive ? 'primary' : 'default'}
                    component={Link}
                    to="/planning"
                    disableElevation
                    sx={navButtonSx}
                  >
                    <StyledMenuButtonIcon
                      icon={faCalendarWeek}
                      light={!isPlanningNavActive}
                    />
                    <span
                      style={{
                        color: isPlanningNavActive ? '' : '#408b6a',
                      }}
                    >
                      Planning
                    </span>
                  </Button>
                )}
              </Box>
            )}

            <Box
              sx={{
                flex: '1 1 0',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-end',
                gap: 1,
                minWidth: 0,
              }}
            >
              <Box
                sx={{
                  display: { xs: 'none', sm: 'flex' },
                  alignItems: 'center',
                  gap: 1,
                }}
              >
                {isPlanningNavActive ? <PlanningPresenceAvatars /> : null}
                {!user.is_external_accountant && (
                  <>
                    <Badge
                      color="error"
                      badgeContent={messageQueueCount}
                      variant="dot"
                    >
                      <Button
                        variant="contained"
                        color="default"
                        onClick={handleMenu2}
                        disableElevation
                        sx={{
                          boxShadow: 'none',
                          pointerEvents: toolsMenuOpen ? 'none' : 'auto',
                        }}
                      >
                        <StyledMenuButtonIcon icon={faCog} />
                        Outils
                      </Button>
                    </Badge>
                    <Menu
                      id="menu-appbar-2"
                      anchorEl={anchorEl2}
                      open={toolsMenuOpen}
                      onClose={handleClose}
                      transitionDuration={0}
                    >
                      <MenuItem
                        onClick={handleToolsNavigate('/products/catalog')}
                        sx={{
                          fontWeight: 'bold',
                          backgroundColor: 'primary.main',
                          color: 'primary.contrastText',
                          '&:hover': {
                            backgroundColor: 'primary.dark',
                            color: 'primary.contrastText',
                          },
                        }}
                      >
                        Catalogue des produits
                      </MenuItem>
                      <Divider />
                      {/* two next elements should be visible only if user.is_superuser or user.is_product_manager */}
                      {(user.is_superuser || user.is_product_manager) && [
                        <MenuItem
                          key="a"
                          onClick={handleToolsNavigate('/admin/products')}
                          sx={{
                            fontWeight: 'bold',
                            backgroundColor: 'error.main',
                            color: 'error.contrastText',
                            '&:hover': {
                              backgroundColor: 'error.dark',
                              color: 'error.contrastText',
                            },
                          }}
                        >
                          Produits
                        </MenuItem>,
                        <Divider key="b" />,
                        <MenuItem
                          key="c"
                          onClick={handleToolsNavigate('/quotes-validation')}
                          sx={{
                            fontWeight: 'bold',
                            backgroundColor: 'error.main',
                            color: 'error.contrastText',
                            '&:hover': {
                              backgroundColor: 'error.dark',
                              color: 'error.contrastText',
                            },
                          }}
                        >
                          Validation des devis
                        </MenuItem>,
                        <Divider key="d" />,
                      ]}
                      <MenuItem onClick={handleToolsNavigate('/requests')}>
                        Nouvelle demande
                      </MenuItem>
                      <Divider />
                      <MenuItem
                        onClick={handleToolsNavigate('/messagequeuemanager')}
                      >
                        <StyledBadge
                          color="error"
                          badgeContent={messageQueueCount}
                        >
                          <span>E-mails/SMS</span>
                        </StyledBadge>
                      </MenuItem>
                      <Divider />
                      {(user.is_superuser ||
                        user.is_project_manager ||
                        user.is_admin_leader ||
                        user.is_external_accountant ||
                        user.is_calendar_manager) && [
                        <MenuItem
                          onClick={handleToolsNavigate('/interventions')}
                          key="a"
                        >
                          Interventions
                        </MenuItem>,
                        <Divider key="b" />,
                      ]}

                      {(user.is_visible_on_planning ||
                        user.is_superuser ||
                        user.is_planning_leader) && [
                        <MenuItem
                          onClick={handleToolsNavigate('/my-planning')}
                          key="my-planning"
                        >
                          Mon planning
                        </MenuItem>,
                        <Divider key="my-planning-divider" />,
                      ]}
                      {!user.is_blocked_from_company_planning && [
                        <MenuItem
                          onClick={handleToolsNavigate(
                            '/technicians-unavailabilities',
                          )}
                          key="tech-unavail"
                        >
                          Congés / Indispo. techs
                        </MenuItem>,
                        <Divider key="tech-unavail-divider" />,
                      ]}
                      <MenuItem
                        onClick={handleToolsNavigate('/construction-review')}
                        key="a"
                      >
                        <span>Fin de chantier</span>
                      </MenuItem>
                      <Divider key="b" />
                      {(user.is_superuser ||
                        user.is_marketing ||
                        user.is_marketing_only ||
                        user.is_commercial_manager) && (
                        <MenuItem
                          onClick={handleToolsNavigate('/v2/internet-offers')}
                        >
                          Offres internet
                        </MenuItem>
                      )}
                      {(user.is_superuser ||
                        user.is_marketing ||
                        user.is_marketing_only ||
                        user.is_commercial_manager) && [
                        <Divider key="a" />,
                        <MenuItem
                          onClick={handleToolsNavigate('/quote-variants')}
                          key="b"
                        >
                          Variantes sur devis
                        </MenuItem>,
                      ]}
                      {(user.is_user_manager || user.is_superuser) && [
                        <Divider key="users-divider" />,
                        <MenuItem
                          onClick={handleToolsNavigate('/admin/users')}
                          key="users"
                        >
                          Utilisateurs
                        </MenuItem>,
                      ]}
                      {((user.is_staff && user.is_admin_leader) ||
                        user.is_superuser) && [
                        <Divider key="a" />,
                        <MenuItem
                          onClick={handleToolsNavigate('/order-sync')}
                          key="b"
                        >
                          Synchro. Google Sheets
                        </MenuItem>,
                      ]}
                      {user.is_superuser && [
                        <Divider key="a" />,
                        <MenuItem
                          onClick={handleToolsNavigate('/admin/reload-offers')}
                          key="b"
                        >
                          Synchro. des offres
                        </MenuItem>,
                      ]}
                      {user.is_superuser && [
                        <Divider key="a" />,
                        <MenuItem
                          onClick={handleToolsNavigate(
                            '/admin/special-discounts',
                          )}
                          key="b"
                        >
                          Remises spéciales
                        </MenuItem>,
                      ]}
                      {user.is_superuser && [
                        <Divider key="a" />,
                        <MenuItem
                          onClick={handleToolsNavigate(
                            '/admin/virtual-kit-config',
                          )}
                          key="b"
                        >
                          Configs kits virtuels
                        </MenuItem>,
                      ]}
                    </Menu>
                  </>
                )}
                <Button color="inherit" onClick={handleMenu}>
                  <Box mr={1}>
                    <Badge
                      color={isWSConnected ? 'success' : 'disabled'}
                      overlap="circular"
                      badgeContent={1}
                      variant="dot"
                      anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'right',
                      }}
                    >
                      <Avatar
                        alt={`${
                          user.first_name
                        } ${user.last_name[0].toUpperCase()}.`}
                        src={`${getApiUrl()}/avatar/${user.id}`}
                      />
                    </Badge>
                  </Box>
                  {user.first_name} {user.last_name[0].toUpperCase()}.
                </Button>
                <Menu
                  id="menu-appbar"
                  anchorEl={anchorEl}
                  keepMounted
                  open={Boolean(anchorEl)}
                  onClose={handleClose}
                  PaperProps={{
                    sx: {
                      width: 200,
                      maxWidth: '100%',
                    },
                  }}
                  MenuListProps={{
                    sx: {
                      paddingTop: 0,
                    },
                  }}
                >
                  <MenuItem
                    disabled
                    sx={{
                      opacity: '1 !important',
                      bgcolor: 'primary.main',
                      py: 2,
                    }}
                  >
                    <ListItemText>
                      <Typography
                        sx={{
                          fontWeight: 'bold',
                          color: 'primary.contrastText',
                        }}
                      >
                        Manywatts
                      </Typography>
                    </ListItemText>
                    {import.meta.env.VITE_VERSION && (
                      <Typography variant="body2" color="text.secondary">
                        <Chip
                          label={`v${import.meta.env.VITE_VERSION}`}
                          size="small"
                          sx={{
                            bgcolor: 'white',
                          }}
                        />
                      </Typography>
                    )}
                  </MenuItem>
                  <Divider sx={{ mt: '0 !important' }} />
                  <MenuItem
                    component={Link}
                    to="/app/account"
                    onClick={handleClose}
                    disabled
                  >
                    Mon compte
                  </MenuItem>
                  <Divider />
                  <MenuItem onClick={handleLogout}>Se déconnecter</MenuItem>
                </Menu>
              </Box>
              <IconButton
                color="inherit"
                edge="start"
                onClick={handleDrawerToggle}
                sx={{ display: { xs: 'inline-flex', sm: 'none' } }}
              >
                <FontAwesomeIcon icon={faBars} />
              </IconButton>
            </Box>
          </Box>
        </StyledToolbar>
      </AppBar>
      <Box sx={{ minHeight: 48 }} />
      {/* <Toolbar /> */}
    </ThemeProvider>
  );
}

export default MainMenu;
